import React, { useEffect } from "react";
import { Modal, Form, Input, Button, message } from "antd";
import { useUserProfileUpdate } from "../../hooks/api/user/hooks";
import { useUserStore } from "../../store/slices/user";

import { useMessageApi } from "../../provider/AntdNotification";

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ProfileModal: React.FC<ProfileModalProps> = ({ isOpen, onClose }) => {
  const messageApi = useMessageApi();
  const [form] = Form.useForm();
  const { user, updateUser } = useUserStore();

  const updateProfileMutation = useUserProfileUpdate({
    onSuccess: (res) => {
      messageApi.success(res.message || "Profile updated successfully!");
      if (res.data) {
        updateUser(res.data as any);
      }
      onClose();
    },
    onError: (err: any) => {
      messageApi.error(err?.message || "Failed to update profile");
    },
  });

  useEffect(() => {
    if (isOpen && user) {
      form.setFieldsValue({
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || "",
      });
    }
  }, [isOpen, user, form]);

  const handleSubmit = (values: any) => {
    updateProfileMutation.mutate(values);
  };

  return (
    <Modal
      title="Edit Profile"
      open={isOpen}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={updateProfileMutation.isPending}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={updateProfileMutation.isPending}
          onClick={() => form.submit()}
        >
          Save Profile
        </Button>,
      ]}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        className="mt-4"
      >
        <Form.Item
          name="name"
          label="Full Name"
          rules={[
            { required: true, message: "Please enter your name" },
            { min: 2, message: "Name must be at least 2 characters" },
          ]}
        >
          <Input placeholder="Enter full name" />
        </Form.Item>

        <Form.Item
          name="email"
          label="Email Address"
          tooltip="Email address cannot be changed"
        >
          <Input disabled placeholder="Email address" className="bg-slate-100" />
        </Form.Item>

        <Form.Item
          name="phone"
          label="Phone Number"
          rules={[
            { required: true, message: "Please enter your phone number" },
            {
              pattern: /^[0-9+\-\s()]{7,15}$/,
              message: "Please enter a valid phone number",
            },
          ]}
        >
          <Input placeholder="Enter phone number" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ProfileModal;
